import { useCallback } from 'react';
import { graphql, useMutation, useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';
import { importDefaultLibraryMutation } from 'src/components/pages/images/libraryImportTaskQueries';
import { libraryImportTaskQueriesImportDefaultMutation } from 'src/components/pages/images/__generated__/libraryImportTaskQueriesImportDefaultMutation.graphql';
import { useDiscoveryBackgroundGalleryQuery } from './__generated__/useDiscoveryBackgroundGalleryQuery.graphql';

export interface UseDiscoveryBackgroundGalleryReturn {
    /**
     * Network-only check of whether the seller is a "legacy, no gallery" account: they never received the
     * default background gallery import (`hasDefaultBackgroundGallery` false) AND have no background images of
     * their own. Only then does the discovery Fill Background gallery tab render empty and need the import CTA —
     * any seller with a default or personalized gallery already has images the customizer loads live. Never
     * throws; degrades to false (no CTA) on error.
     */
    resolveIsLegacy: () => Promise<boolean>;
    /**
     * Fires the (idempotent) default-library import — the same mutation the backoffice Fill Background gallery
     * offers legacy sellers; it seeds both the image and background galleries. Resolves true once the import is
     * queued server-side (the caller then bumps the blocking overlay's refresh signal so it picks up the new
     * background task and shows its progress bar), false if the mutation failed to start.
     */
    startDefaultGalleryImport: () => Promise<boolean>;
}

/**
 * Discovery-mode Fill Background gallery controller (backoffice side). The customizer has no GraphQL client, so
 * the host owns the gallery decisions: detect the legacy empty-gallery state, and on the merchant's explicit
 * action fire the SAME default-library import the Fill Background setup page runs. The progress bar is the real
 * shared `GalleryImportBlockingOverlay` (rendered by the panel), so nothing is reimplemented here — this hook
 * only reads account state and triggers the import.
 */
export function useDiscoveryBackgroundGallery(): UseDiscoveryBackgroundGalleryReturn {
    const relayEnvironment = useRelayEnvironment();
    const [commitImportDefaultLibrary] = useMutation<libraryImportTaskQueriesImportDefaultMutation>(importDefaultLibraryMutation);

    // network-only so the resolve reflects the live account state rather than a stale cache.
    const fetchGalleryState = useCallback(async () => {
        const data = await fetchQuery<useDiscoveryBackgroundGalleryQuery>(
            relayEnvironment,
            graphql`
                query useDiscoveryBackgroundGalleryQuery {
                    sellerSetting {
                        sellerProperties {
                            hasDefaultBackgroundGallery
                        }
                    }
                    imageMacroCategories(onlybackground: true) {
                        hasImages
                    }
                }
            `,
            {},
            { fetchPolicy: 'network-only' }
        ).toPromise();
        return {
            hasDefault: data?.sellerSetting?.sellerProperties?.hasDefaultBackgroundGallery ?? false,
            hasImages: (data?.imageMacroCategories ?? []).some((category) => category?.hasImages === true)
        };
    }, [relayEnvironment]);

    const resolveIsLegacy = useCallback(async (): Promise<boolean> => {
        try {
            const { hasDefault, hasImages } = await fetchGalleryState();
            return !hasDefault && !hasImages;
        } catch (error) {
            console.error('Discovery background gallery: failed to resolve legacy state:', error);
            return false;
        }
    }, [fetchGalleryState]);

    const startDefaultGalleryImport = useCallback((): Promise<boolean> => {
        return new Promise<boolean>((resolveImport) => {
            commitImportDefaultLibrary({
                variables: {},
                onCompleted: () => resolveImport(true),
                onError: (error) => {
                    console.warn('Discovery background gallery: import failed to start:', error);
                    resolveImport(false);
                }
            });
        });
    }, [commitImportDefaultLibrary]);

    return { resolveIsLegacy, startDefaultGalleryImport };
}

export default useDiscoveryBackgroundGallery;
